import { axiosGet, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { BulkResponseModel } from '@/features/batch/_models/batch';
import type { RequestProjectsParams } from '@/features/project/_hooks/project';
import { BulkCopyFormSchema } from '@/features/project/_models/group';
import { GroupProjectListModel } from '@/features/project/groups/group-detail/_models/group';
import {
  invalidateListMap,
  queryKeyMap,
} from '@/features/project/review-sheet/utils/query-keys';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';

export const useBulkCopy = (id: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationKey: ['bulkCopy'],
    mutationFn: async (values: z.infer<typeof BulkCopyFormSchema>) => {
      const { fiscalYear } = BulkCopyFormSchema.parse(values);
      const res = await axiosPost(`/api/projects/groups/${id}/continue/`, {
        fiscalYear: fiscalYear.year(),
      });
      // TODO バッチ処理終了後にキャッシュを更新する機構
      await Promise.all([
        ...invalidateListMap.updateList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.project()),
      ]);
      return BulkResponseModel.parse(res);
    },
  });
};

export const useBulkLock = (id: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationKey: ['bulkLock', id],
    mutationFn: async () => {
      const res = await axiosPost(`/api/projects/groups/${id}/lock/`, {});
      await Promise.all([
        invalidate(queryKeyMap.projects()),
        invalidate(queryKeyMap.groupProjects()),
        invalidate(queryKeyMap.project()),
      ]);
      return BulkResponseModel.parse(res);
    },
  });
};

export const useBulkUnlock = (id: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationKey: ['bulkUnlock', id],
    mutationFn: async () => {
      const res = await axiosPost(`/api/projects/groups/${id}/unlock/`, {});
      await Promise.all([
        invalidate(queryKeyMap.projects()),
        invalidate(queryKeyMap.groupProjects()),
        invalidate(queryKeyMap.project()),
      ]);
      return BulkResponseModel.parse(res);
    },
  });
};

export const useBulkPublish = (id: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationKey: ['bulkPublish', id],
    mutationFn: async () => {
      const res = await axiosPost(`/api/projects/groups/${id}/publish/`, {});
      await invalidate(queryKeyMap.project());
      return BulkResponseModel.parse(res);
    },
  });
};

export const useBulkUnpublish = (id: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationKey: ['bulkUnpublish', id],
    mutationFn: async () => {
      const res = await axiosPost(`/api/projects/groups/${id}/unpublish/`, {});
      await invalidate(queryKeyMap.project());
      return BulkResponseModel.parse(res);
    },
  });
};

export const useGroupProjects = ({
  id,
  page,
  ordering,
  enabled = true,
}: {
  id: string;
  page: number;
  ordering: string | null;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.groupProjects(id, page, ordering),
    queryFn: async () => {
      const groupSummary = await axiosGet(
        `/api/projects/groups/${id}/projects/`,
        { page, ordering },
      );
      return GroupProjectListModel.parse(groupSummary);
    },
    enabled: enabled && !!id,
  });
};

export const useGroupTargetExtend = (id: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: RequestProjectsParams) => {
      const res = await axiosPost(
        `/api/projects/groups/${id}/projects/`,
        values,
      );
      await Promise.all([
        invalidate(queryKeyMap.projectIds()),
        invalidate(queryKeyMap.projectGroupSummary(id)),
        invalidate(queryKeyMap.groupProjects(id)),
        invalidate(queryKeyMap.group(id)),
      ]);
      return res;
    },
  });
};
